import api from '@dashboard/services/axiosApi'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import type { Partner } from '@dashboard/types/partner'

export interface PartnerDataItem {
  id: string | number
  label: string
  name?: string
  key?: string
  templatingToken?: string
  dataType?: string
  enumerated?: boolean
  allowMultiple?: boolean
  options?: (string | { value: string; label: string })[]
  optionsNote?: string
}

export interface PartnerEvent {
  name: string
}

export interface PartnerProducts {
  products: string[]
  supportsRevenue: boolean
}

/**
 * Helper to determine if a partner uses V2 endpoints
 * V2 is used when integrationVersion is 'v2' or 'prefer_v2' (dual providers default to V2)
 */
const isV2Integration = (partner: Partner | undefined): boolean =>
  partner?.integrationVersion === 'v2' || partner?.integrationVersion === 'prefer_v2'

export default {
  createCustomField: {
    mutationFn: (teamPid: number, label: string) => {
      const { integratedPartner } = useIntegrationStore()

      const endpoint = isV2Integration(integratedPartner)
        ? `/api/${teamPid}/connections/${integratedPartner?.name}/custom_fields`
        : `/api/${teamPid}/integrations/${integratedPartner?.name}/custom_fields`

      return api.post(endpoint, { label })
    }
  },
  native_fields: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      const customField = integratedPartner?.espFeatureSupport?.customField
      if (typeof customField === 'object' && customField?.unstructured) {
        return []
      }

      if (isV2Integration(integratedPartner)) {
        return api
          .get(`/api/${teamPid}/connections/${integratedPartner?.name}/native_fields`)
          .then((response) => response.data.native_fields)
      }

      return api
        .get(`/api/${teamPid}/integrations/${integratedPartner?.name}/native_fields`)
        .then((response) => response.data.native_fields)
    }
  },
  custom_fields: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      const customField = integratedPartner?.espFeatureSupport?.customField
      if (typeof customField === 'object' && customField?.unstructured) {
        return []
      }

      if (isV2Integration(integratedPartner)) {
        return api
          .get(`/api/${teamPid}/connections/${integratedPartner?.name}/custom_fields`)
          .then((response) => response.data.custom_fields)
      }

      return api
        .get(`/api/${teamPid}/integrations/${integratedPartner?.name}/custom_fields`)
        .then((response) => response.data.custom_fields)
    }
  },
  lists: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.list) {
        return []
      }

      if (isV2Integration(integratedPartner)) {
        return api
          .get(`/api/${teamPid}/connections/${integratedPartner?.name}/lists`)
          .then((response) => response.data.lists)
      }

      return api
        .get(`/api/${teamPid}/integrations/${integratedPartner?.name}/lists`)
        .then((response) => response.data.lists)
    }
  },
  tags: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.tag) {
        return []
      }

      if (isV2Integration(integratedPartner)) {
        return api
          .get(`/api/${teamPid}/connections/${integratedPartner?.name}/tags`)
          .then((response) => response.data.tags)
      }

      return api
        .get(`/api/${teamPid}/integrations/${integratedPartner?.name}/tags`)
        .then((response) => response.data.tags)
    }
  },
  forms: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (isV2Integration(integratedPartner)) {
        return api
          .get(`/api/${teamPid}/connections/${integratedPartner?.name}/forms`)
          .then((response) => response.data.forms)
      }

      return api
        .get(`/api/${teamPid}/integrations/${integratedPartner?.name}/forms`)
        .then((response) => response.data.forms)
    }
  },
  events: {
    // List of custom event *definitions* the customer can fire (NOT event
    // history — that's `available_events`). Only providers with structured
    // event names surface a non-empty list here (HubSpot). Freeform-event
    // providers (default) return empty and the UI falls back to a text input.
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.event) {
        return []
      }

      // Providers with `arbitrary: true` are freeform-event — no dropdown list to show.
      if (
        typeof integratedPartner.espFeatureSupport.event === 'object' &&
        integratedPartner.espFeatureSupport.event.arbitrary
      ) {
        return []
      }

      // Only V2 integrations expose getEvents() today.
      if (!isV2Integration(integratedPartner)) {
        return []
      }

      return api
        .get(`/api/${teamPid}/connections/${integratedPartner?.name}/events`)
        .then((response) => response.data.events)
    }
  },
  available_events: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.eventHistory) {
        return []
      }

      // Only V2 integrations support event history
      if (!isV2Integration(integratedPartner)) {
        return []
      }

      return api
        .get(`/api/${teamPid}/connections/${integratedPartner?.name}/available-events`)
        .then((response) => response.data.events)
    }
  },
  available_products: {
    queryFn: (teamPid: number) => {
      const { integratedPartner } = useIntegrationStore()

      if (!integratedPartner?.espFeatureSupport?.purchaseHistory) {
        return { products: [], supportsRevenue: false }
      }

      // Only V2 integrations support purchase history
      if (!isV2Integration(integratedPartner)) {
        return { products: [], supportsRevenue: false }
      }

      return api.get(`/api/${teamPid}/connections/${integratedPartner?.name}/available-products`).then((response) => ({
        products: response.data.products,
        supportsRevenue: response.data.supportsRevenue
      }))
    }
  }
}
